import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that displays total weight for decorative stones products.
 * Calculates: quantity × variant weight
 * Updates when variant changes or quantity changes.
 */
class DSTotalWeight extends HTMLElement {
  constructor() {
    super();
    this.currentVariantWeight = null;
    this.currentQuantity = 1;
  }

  connectedCallback() {
    // Get initial variant weight from data attribute
    const weightValue = this.dataset.variantWeight;
    if (weightValue && weightValue !== '') {
      this.currentVariantWeight = parseFloat(weightValue);
      if (isNaN(this.currentVariantWeight)) {
        this.currentVariantWeight = null;
      }
    } else {
      this.currentVariantWeight = null;
    }
    this.currentQuantity = parseInt(this.dataset.defaultQuantity) || 1;
    
    // Debug logging (remove in production)
    if (!this.currentVariantWeight) {
      console.warn('DS Total Weight: No variant weight found. Check that variant metafield custom.weight is set.');
      console.log('Variant ID:', this.dataset.variantId);
      console.log('Product ID:', this.dataset.productId);
    }
    
    // Update initial display
    this.updateWeightDisplay();

    // Listen for variant updates
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    }

    // Listen for quantity changes
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQuantityUpdate);

    // Also listen for input changes on quantity selector (fallback)
    this.setupQuantityListener();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    }
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQuantityUpdate);
  }

  /**
   * Sets up a listener for quantity input changes as a fallback
   */
  setupQuantityListener() {
    // Find quantity input in the same section
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    // Use MutationObserver to watch for quantity input changes
    const observer = new MutationObserver(() => {
      this.updateQuantityFromInput();
    });

    observer.observe(closestSection, {
      childList: true,
      subtree: true
    });

    // Also check periodically for quantity input
    const checkQuantity = () => {
      this.updateQuantityFromInput();
    };

    // Check on input events
    closestSection.addEventListener('input', (e) => {
      if (e.target.matches && e.target.matches('quantity-selector-component input[ref="quantityInput"]')) {
        const quantity = parseInt(e.target.value) || 1;
        if (quantity !== this.currentQuantity) {
          this.currentQuantity = quantity;
          this.updateWeightDisplay();
        }
      }
    });
  }

  /**
   * Updates quantity from the quantity input element
   */
  updateQuantityFromInput() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    const quantityInput = closestSection.querySelector('quantity-selector-component input[ref="quantityInput"]');
    if (quantityInput) {
      const quantity = parseInt(quantityInput.value) || 1;
      if (quantity !== this.currentQuantity) {
        this.currentQuantity = quantity;
        this.updateWeightDisplay();
      }
    } else {
      // If no quantity input found, reset to default
      const defaultQty = parseInt(this.dataset.defaultQuantity) || 1;
      if (this.currentQuantity !== defaultQty) {
        this.currentQuantity = defaultQty;
        this.updateWeightDisplay();
      }
    }
  }

  /**
   * Handles variant update events
   * @param {VariantUpdateEvent} event
   */
  handleVariantUpdate = (event) => {
    // Check if this update is for our product
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId && event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    // Try to get variant weight from the new HTML's ds-total-weight element
    const newWeightElement = event.detail.data.html?.querySelector('ds-total-weight[data-variant-weight]');
    if (newWeightElement) {
      const weightValue = newWeightElement.dataset.variantWeight;
      if (weightValue && weightValue !== '') {
        const newWeight = parseFloat(weightValue);
        if (!isNaN(newWeight) && newWeight > 0) {
          this.currentVariantWeight = newWeight;
          this.dataset.variantWeight = newWeight.toString();
          this.dataset.variantId = newWeightElement.dataset.variantId || '';
        } else {
          this.currentVariantWeight = null;
          this.dataset.variantWeight = '';
        }
      } else {
        // Try to extract weight from the weight value element in the HTML
        const weightValueElement = event.detail.data.html?.querySelector('ds-total-weight .ds-total-weight-value[data-weight]');
        if (weightValueElement) {
          const weightFromElement = parseFloat(weightValueElement.dataset.weight);
          if (!isNaN(weightFromElement) && weightFromElement > 0) {
            this.currentVariantWeight = weightFromElement;
            this.dataset.variantWeight = weightFromElement.toString();
          } else {
            this.currentVariantWeight = null;
            this.dataset.variantWeight = '';
          }
        } else {
          // Weight not found in HTML either
          this.currentVariantWeight = null;
          this.dataset.variantWeight = '';
        }
      }
    }

    // Update quantity from the new HTML if available
    const newQuantityInput = event.detail.data.html?.querySelector('quantity-selector-component input[ref="quantityInput"]');
    if (newQuantityInput) {
      const quantity = parseInt(newQuantityInput.value) || 1;
      this.currentQuantity = quantity;
      this.updateWeightDisplay();
    } else {
      // Fallback: get quantity from current DOM
      this.updateQuantityFromInput();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event
   */
  handleQuantityUpdate = (event) => {
    // Only update if it's from the same product form
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    // Check if the event came from a quantity selector in our section
    const quantityInput = closestSection.querySelector('quantity-selector-component input[ref="quantityInput"]');
    if (quantityInput) {
      const quantity = parseInt(event.detail.quantity) || 1;
      if (quantity !== this.currentQuantity) {
        this.currentQuantity = quantity;
        this.updateWeightDisplay();
      }
    }
  };

  /**
   * Updates the weight display
   */
  updateWeightDisplay() {
    const weightContainer = this.querySelector('[ref="weightContainer"]');
    if (!weightContainer) return;

    if (!this.currentVariantWeight || isNaN(this.currentVariantWeight) || this.currentVariantWeight <= 0) {
      // Try to extract weight from existing weight value element if it exists
      const existingWeightValue = weightContainer.querySelector('.ds-total-weight-value[data-weight]');
      if (existingWeightValue) {
        const weightFromElement = parseFloat(existingWeightValue.dataset.weight);
        if (!isNaN(weightFromElement) && weightFromElement > 0) {
          this.currentVariantWeight = weightFromElement;
          this.dataset.variantWeight = weightFromElement.toString();
          // Recalculate with this weight
          this.renderWeightAndBoxInfo();
          return;
        }
      }
      // No weight available
      weightContainer.innerHTML = '<span class="ds-total-weight-empty" data-no-weight="true">Weight not available</span>';
      return;
    }

    this.renderWeightAndBoxInfo();
  }

  /**
   * Renders both weight and box/pallet information
   */
  renderWeightAndBoxInfo() {
    const weightContainer = this.querySelector('[ref="weightContainer"]');
    if (!weightContainer) return;

    const totalWeight = this.currentVariantWeight * this.currentQuantity;
    
    // Format weight (show up to 2 decimal places if needed, otherwise show as integer)
    let formattedWeight;
    if (totalWeight % 1 === 0) {
      formattedWeight = totalWeight.toString();
    } else {
      // Round to 2 decimal places and remove trailing zeros
      formattedWeight = totalWeight.toFixed(2).replace(/\.?0+$/, '');
    }

    // Calculate delivery method based on total weight
    let deliveryMessage;
    if (totalWeight <= 120) {
      // 6 boxes or less (20kg per box)
      const boxes = Math.ceil(totalWeight / 20);
      if (boxes <= 1) {
        deliveryMessage = 'Delivered in a single box for your convenience.';
      } else {
        deliveryMessage = `Delivered in ${boxes} carefully packed boxes.`;
      }
    } else if (totalWeight <= 1000) {
      // More than 6 boxes but <= 1000kg = single pallet
      deliveryMessage = 'Securely delivered on a single pallet.';
    } else {
      // More than 1000kg = calculate pallets (1000kg per pallet)
      const pallets = Math.ceil(totalWeight / 1000);
      if (pallets === 1) {
        deliveryMessage = 'Delivered on one premium-grade pallet.';
      } else {
        deliveryMessage = `Delivered on ${pallets} premium-grade pallets.`;
      }
    }

    weightContainer.innerHTML = `
      <div class="ds-weight-info">
        <span class="ds-total-weight-label">Total Weight: </span>
        <span class="ds-total-weight-value" data-weight="${this.currentVariantWeight}">${formattedWeight}</span>
        <span class="ds-total-weight-unit">kg</span>
      </div>
      <div ref="boxInfo" class="ds-box-info">
        <span class="ds-box-message">${deliveryMessage}</span>
      </div>
    `;
  }
}

if (!customElements.get('ds-total-weight')) {
  customElements.define('ds-total-weight', DSTotalWeight);
}

